<template>
  <div class="language-learning-page">
    <!-- Luna Assistant Header -->
    <div class="luna-header">
      <div class="container-fluid">
        <div class="row align-items-center">
          <div class="col-md-8">
            <div class="d-flex align-items-center">
              <div class="luna-avatar me-3">
                <div class="luna-character">
                  <div class="luna-face">
                    <div class="luna-eyes">
                      <div class="eye"></div>
                      <div class="eye"></div>
                    </div>
                    <div class="luna-mouth">😊</div>
                  </div>
                  <div class="luna-sparkles">
                    <span class="sparkle sparkle-1">✨</span>
                    <span class="sparkle sparkle-2">⭐</span>
                    <span class="sparkle sparkle-3">✨</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 class="mb-1 fw-bold">{{ lunaGreeting }}</h4>
                <p class="mb-0 text-muted">{{ lunaSubtext }}</p>
              </div>
            </div>
          </div>
          <div class="col-md-4 text-end">
            <div class="d-flex gap-2 align-items-center">
              <!-- Voice Settings Button -->
              <button 
                class="btn btn-outline-primary btn-sm voice-settings-btn"
                @click="showVoiceSettings = !showVoiceSettings"
                :class="{ 'active': showVoiceSettings }"
                title="Luna's Voice Settings"
              >
                <i class="bi bi-megaphone"></i>
              </button>
              
              <!-- Level Selector -->
              <select class="form-select level-selector" v-model="selectedLevel" @change="onLevelChange">
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Voice Settings Panel -->
    <div v-if="showVoiceSettings" class="voice-settings-panel">
      <div class="container-fluid">
        <div class="voice-panel-content">
          <h6 class="voice-title mb-3">
            <i class="bi bi-megaphone me-2"></i>Luna's Voice Settings
          </h6>
          
          <div class="row">
            <div class="col-md-8">
              <!-- Voice Selection -->
              <div class="voice-selection mb-3">
                <label class="form-label small fw-bold">Choose Luna's Voice:</label>
                <select 
                  class="form-select form-select-sm"
                  v-model="selectedVoiceName"
                  @change="changeVoice"
                >
                  <option value="">Auto-select best voice</option>
                  <option 
                    v-for="voice in availableVoices" 
                    :key="voice.name"
                    :value="voice.name"
                  >
                    {{ voice.name }} 
                    <span v-if="voice.isChildFriendly">⭐</span>
                  </option>
                </select>
                <small class="text-muted">⭐ = Especially child-friendly</small>
              </div>
              
              <!-- Voice Parameters -->
              <div class="voice-parameters">
                <div class="row">
                  <div class="col-md-6">
                    <label class="form-label small">Voice Pitch:</label>
                    <input 
                      type="range" 
                      class="form-range"
                      min="1.0" 
                      max="1.5" 
                      step="0.1"
                      v-model="voiceSettings.pitch"
                      @input="updateVoiceSettings"
                    >
                    <small class="text-muted">{{ voiceSettings.pitch }} (Higher = More Youthful)</small>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label small">Speaking Speed:</label>
                    <input 
                      type="range" 
                      class="form-range"
                      min="0.8" 
                      max="1.2" 
                      step="0.05"
                      v-model="voiceSettings.rate"
                      @input="updateVoiceSettings"
                    >
                    <small class="text-muted">{{ voiceSettings.rate }} (Lower = Clearer)</small>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="col-md-4 text-center">
              <!-- Voice Preview -->
              <div class="voice-preview">
                <button 
                  class="btn btn-primary btn-sm mb-2"
                  @click="previewCurrentVoice"
                  :disabled="loading.voicePreview"
                >
                  <i class="bi bi-play-fill me-1" v-if="!loading.voicePreview"></i>
                  <span v-if="loading.voicePreview" class="spinner-border spinner-border-sm me-1"></span>
                  Listen to Luna
                </button>
                <br>
                <small class="text-muted">
                  Current: {{ currentVoiceInfo.selectedVoice?.name || 'Auto-selected' }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="container-fluid">
      <!-- Learning Mode Tabs -->
      <div class="learning-tabs mb-4">
        <button 
          v-for="tab in learningTabs" 
          :key="tab.id"
          class="tab-button"
          :class="{ active: selectedTab === tab.id }"
          @click="selectTab(tab.id)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="row g-4">
        <!-- Practice Section -->
        <div class="col-lg-8">
          <div class="practice-section">
            <div class="section-header mb-4">
              <h5 class="section-title" v-if="selectedTab === 'pronunciation'">Practice Your Words!</h5>
              <h5 class="section-title" v-else-if="selectedTab === 'dictation-en'">Dictation (EN)</h5>
              <h5 class="section-title" v-else>Dictation (HI)</h5>
              <div v-if="loading.words" class="text-center">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading words...</span>
                </div>
              </div>
            </div>

            <!-- Pronunciation Word List -->
            <div class="word-list mb-4" v-if="!loading.words && selectedTab === 'pronunciation'">
              <div 
                v-for="word in practiceWords" 
                :key="word.id"
                class="word-item"
                :class="{ 
                  completed: word.completed,
                  active: currentWord && currentWord.id === word.id
                }"
                @click="selectWord(word)"
              >
                <div class="word-content">
                  <span class="word-text">{{ word.word }}</span>
                  <span class="word-definition" v-if="word.definition">{{ word.definition }}</span>
                </div>
                <div class="word-actions">
                  <button 
                    class="btn btn-play" 
                    @click.stop="playWord(word)"
                    :disabled="loading.audio"
                  >
                    <i class="bi bi-play-fill" v-if="!loading.audio"></i>
                    <div v-else class="spinner-border spinner-border-sm" role="status"></div>
                  </button>
                  <div v-if="word.accuracy_score" class="accuracy-badge">
                    {{ word.accuracy_score }}%
                  </div>
                </div>
              </div>
              
              <div v-if="practiceWords.length === 0" class="text-center text-muted py-4">
                <p>No words available for this level. Try a different difficulty!</p>
              </div>
            </div>

            <!-- Dictation (EN) UI -->
            <div v-if="selectedTab === 'dictation-en'" class="voice-interface">
              <div class="mb-3">
                <p class="text-center mb-2" v-if="dictation && dictation.current">
                  Listen carefully and type exactly what you hear.
                </p>
                <div class="d-flex justify-content-center gap-2 mb-3">
                  <button class="btn btn-primary" @click="playDictation(false)" :disabled="loading.audio">
                    <i class="bi bi-play-fill"></i> Play
                  </button>
                  <button class="btn btn-secondary" @click="playDictation(true)" :disabled="loading.audio">
                    <i class="bi bi-skip-backward"></i> Slow (0.85x)
                  </button>
                </div>
                <input
                  v-model="dictation.answer"
                  type="text"
                  class="form-control text-center"
                  placeholder="Type what you heard..."
                  @keyup.enter="submitDictation"
                />
                <div class="mt-3 d-flex justify-content-center gap-2">
                  <button class="btn btn-success" @click="submitDictation">Check</button>
                  <button class="btn btn-outline-primary" @click="nextDictationItem">Next</button>
                </div>
                <div v-if="dictation.lastScore !== null" class="mt-3">
                  <div class="fw-bold">Score: {{ dictation.lastScore }}%</div>
                  <small v-if="dictation.current">Target: "{{ dictation.current.prompt }}"</small>
                </div>
              </div>
            </div>

            <!-- Voice Recording Interface (Pronunciation) -->
            <div v-else class="voice-interface">
              <div class="current-word-display mb-3" v-if="currentWord">
                <h4 class="text-center mb-2">Practice: "{{ currentWord.word }}"</h4>
                <p class="text-center text-muted">{{ currentWord.example_sentence }}</p>
              </div>

              <div class="microphone-container text-center mb-4">
                <button 
                  class="microphone-btn"
                  :class="{ 
                    recording: isRecording,
                    processing: loading.speechProcessing 
                  }"
                  @click="toggleRecording"
                  :disabled="!currentWord || loading.speechProcessing"
                >
                  <i class="bi bi-mic-fill" v-if="!isRecording && !loading.speechProcessing"></i>
                  <i class="bi bi-stop-fill" v-else-if="isRecording && !loading.speechProcessing"></i>
                  <div v-else class="spinner-border" role="status"></div>
                </button>
                <p class="mt-3 mb-0">{{ recordingText }}</p>
              </div>

              <!-- Pronunciation Result -->
              <div v-if="pronunciationResult" class="pronunciation-result mb-4">
                <div class="accuracy-score">
                  <div class="score-circle" :class="getScoreClass(pronunciationResult.accuracy_score)">
                    <span class="score-number">{{ pronunciationResult.accuracy_score }}%</span>
                  </div>
                </div>
                <div class="feedback">
                  <h6>Luna's Feedback:</h6>
                  <p>{{ pronunciationResult.feedback }}</p>
                  <div v-if="pronunciationResult.tips && pronunciationResult.tips.length > 0">
                    <h6>Tips:</h6>
                    <ul>
                      <li v-for="tip in pronunciationResult.tips" :key="tip">{{ tip }}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="action-buttons">
                <button 
                  class="btn btn-secondary"
                  @click="practiceWithLuna"
                  :disabled="!currentWord || loading.speechProcessing"
                >
                  Say It With Me!
                </button>
                <button 
                  class="btn btn-primary"
                  @click="nextWord"
                  :disabled="practiceWords.length === 0"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-lg-4">
          <!-- Luna Chat -->
          <div class="luna-chat-card mb-4">
            <h6 class="chat-title mb-3">CHAT WITH LUNA</h6>
            <div class="chat-messages" ref="chatMessages">
              <div 
                v-for="message in chatMessages" 
                :key="message.id"
                class="chat-message"
                :class="{ 'user-message': message.isUser, 'luna-message': !message.isUser }"
              >
                <div class="message-content">{{ message.content }}</div>
              </div>
              <div v-if="loading.lunaChat" class="chat-message luna-message">
                <div class="message-content">
                  <div class="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            </div>
            <div class="chat-input">
              <input 
                type="text" 
                v-model="chatInput" 
                @keyup.enter="sendChatMessage"
                placeholder="Ask Luna anything... or click 🎤 to speak!"
                :disabled="loading.lunaChat || isVoiceRecording"
                class="form-control"
              >
              
              <!-- Voice Recording Button -->
              <button 
                @click="toggleVoiceRecording"
                :disabled="loading.lunaChat"
                class="btn btn-voice"
                :class="{ 
                  'btn-danger': isVoiceRecording, 
                  'btn-outline-primary': !isVoiceRecording,
                  'recording': isVoiceRecording 
                }"
                :title="isVoiceRecording ? 'Stop recording' : 'Start voice recording'"
              >
                <i :class="isVoiceRecording ? 'bi bi-stop-fill' : 'bi bi-mic-fill'"></i>
                <span v-if="isVoiceRecording" class="recording-pulse"></span>
              </button>
              
              <!-- Volume Meter (shown during recording) -->
              <div v-if="isVoiceRecording" class="volume-meter">
                <div class="volume-bar" :style="{ width: volumeLevel + '%' }"></div>
              </div>
              
              <button 
                @click="sendChatMessage" 
                :disabled="!chatInput.trim() || loading.lunaChat || isVoiceRecording"
                class="btn btn-primary btn-sm"
              >
                Send
              </button>
            </div>
          </div>

          <!-- Luna's Tips -->
          <div class="tips-card mb-4">
            <h6 class="tips-title mb-3">LUNA'S TIPS</h6>
            <div v-if="loading.tips" class="text-center">
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </div>
            <ul class="tips-list" v-else>
              <li v-for="tip in lunaTips" :key="tip">{{ tip }}</li>
            </ul>
            <button 
              class="btn btn-outline-primary btn-sm mt-2" 
              @click="refreshTips"
              :disabled="loading.tips"
            >
              Refresh Tips
            </button>
          </div>

          <!-- Progress Overview -->
          <div class="progress-card">
            <h6 class="progress-title mb-3">PROGRESS OVERVIEW</h6>
            <div v-if="loading.progress" class="text-center">
              <div class="spinner-border spinner-border-sm" role="status"></div>
            </div>
            <div class="progress-stats" v-else>
              <div class="stat-item">
                <span class="stat-number">{{ (userProgress && userProgress.words_mastered) || 0 }}/{{ totalWords }}</span>
                <span class="stat-label">words mastered</span>
              </div>
              <div class="progress-bar-container">
                <div 
                  class="progress-bar-fill" 
                  :style="{ width: progressPercentage + '%' }"
                ></div>
              </div>
              <div class="level-badge">
                Level {{ (userProgress && userProgress.current_level) || 1 }}
              </div>
              <div class="stats-grid mt-3">
                <div class="stat-small">
                                      <span class="stat-value">{{ (userProgress && userProgress.total_points) || 0 }}</span>
                  <span class="stat-label">Points</span>
                </div>
                <div class="stat-small">
                                      <span class="stat-value">{{ Math.round((userProgress && userProgress.average_accuracy) || 0) }}%</span>
                  <span class="stat-label">Accuracy</span>
                </div>
              </div>
            </div>

            <!-- Streak Badge -->
                          <div class="streak-badge" v-if="userProgress && userProgress.current_streak > 0">
                <div class="streak-number">{{ userProgress.current_streak }}-Day</div>
              <div class="streak-label">Streak!</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import languageLearningService from '@/services/languageLearningService'
import voiceRecordingService from '@/services/voiceRecordingService'
import toast from '@/services/toastService'

export default {
  name: 'LanguageLearningPage',
  data() {
    return {
      // User and session data
      userId: 1, // TODO: Get from auth state
      selectedLevel: 'beginner',
      selectedTab: 'pronunciation',
      
      // Recording state (legacy pronunciation)
      isRecording: false,
      mediaRecorder: null,
      audioChunks: [],
      stream: null,
      
      // Voice recording state (Whisper integration)
      isVoiceRecording: false,
      volumeLevel: 0,
      transcriptionResult: null,
      
      // Current practice state
      currentWord: null,
      currentWordIndex: 0,
      pronunciationResult: null,
      
      // Data arrays
      practiceWords: [],
      lunaTips: [],
      chatMessages: [],
      
      // UI state
      chatInput: '',
      
      // Loading states
      loading: {
        words: false,
        progress: false,
        tips: false,
        audio: false,
        speechProcessing: false,
        lunaChat: false,
        voicePreview: false
      },
      
      // Voice settings
      showVoiceSettings: false,
      selectedVoiceName: '',
      availableVoices: [],
      voiceSettings: {
        pitch: 1.3,
        rate: 0.95,
        volume: 0.8
      },
      currentVoiceInfo: {},
      
      // Progress data
      userProgress: {
        words_mastered: 0,
        current_level: 1,
        total_points: 0,
        average_accuracy: 0,
        current_streak: 0
      },
      
      // Configuration
      learningTabs: [
        { id: 'pronunciation', label: 'Pronunciation' },
        { id: 'dictation-en', label: 'Dictation (EN)' },
        { id: 'dictation-hi', label: 'Dictation (HI)' }
      ],
      
      // Session tracking
      sessionStartTime: null,
      sessionData: {
        wordsAttempted: 0,
        correctPronunciations: 0,
        totalAttempts: 0
      },
      masteredWordIds: new Set()
    }
  },
  
  computed: {
    lunaGreeting() {
      const hour = new Date().getHours()
      if (hour < 12) return "Good morning! Ready to learn?"
      if (hour < 17) return "Good afternoon! Let's practice!"
      return "Good evening! Time for some learning!"
    },
    
    lunaSubtext() {
      return "I'll help you practice speaking, listening, and understanding new words."
    },
    
    recordingText() {
      if (this.loading.speechProcessing) return 'Processing your speech...'
      if (this.isRecording) return 'Listening... Speak now!'
      if (!this.currentWord) return 'Select a word to practice!'
      return 'Tap and speak the word!'
    },
    
    progressPercentage() {
      if (!this.userProgress || !this.userProgress.words_mastered || !this.totalWords) return 0
      return Math.round((this.userProgress.words_mastered / this.totalWords) * 100)
    },
    
    totalWords() {
      return this.practiceWords.length
    }
  },
  
  async mounted() {
    this.sessionStartTime = new Date()
    await this.initializeData()
    await this.initializeVoiceSettings()
    this.addLunaMessage("Hello! I'm Luna, your language learning assistant. Pick a word and let's start practicing! 🌟")
  },
  
  beforeUnmount() {
    this.stopRecordingCleanup()
    // Clean up voice recording service
    voiceRecordingService.cleanup()
    this.saveSession()
  },
  
  methods: {
    async initializeData() {
      await Promise.all([
        this.loadWords(),
        this.loadProgress(),
        this.loadTips()
      ])
      
      if (this.practiceWords.length > 0) {
        this.currentWord = this.practiceWords[0]
      }
    },
    
    async loadWords() {
      this.loading.words = true
      try {
        const response = await languageLearningService.getWords({
          difficulty: this.getDifficultyLevel(),
          language: 'en',
          limit: 20
        })
        
        if (response.success) {
          this.practiceWords = response.words.map(word => ({
            ...word,
            completed: false,
            accuracy_score: null
          }))
        }
      } catch (error) {
        console.error('Error loading words:', error)
      } finally {
        this.loading.words = false
      }
    },
    
    async loadProgress() {
      this.loading.progress = true
      try {
        const response = await languageLearningService.getProgress(this.userId)
        if (response.success) {
          this.userProgress = response.progress
        }
      } catch (error) {
        console.error('Error loading progress:', error)
      } finally {
        this.loading.progress = false
      }
    },
    
    async loadTips() {
      this.loading.tips = true
      try {
        const response = await languageLearningService.getLunaTips(this.userId, this.selectedLevel)
        if (response.success) {
          this.lunaTips = response.tips
        }
      } catch (error) {
        console.error('Error loading tips:', error)
      } finally {
        this.loading.tips = false
      }
    },
    
    getDifficultyLevel() {
      const levelMap = {
        'beginner': 1,
        'intermediate': 2,
        'advanced': 3
      }
      return levelMap[this.selectedLevel] || 1
    },
    
    async onLevelChange() {
      await this.loadWords()
      await this.loadTips()
      if (this.practiceWords.length > 0) {
        this.currentWord = this.practiceWords[0]
        this.currentWordIndex = 0
      }
    },
    
    selectTab(tabId) {
      this.selectedTab = tabId
      // Reset state specific to modes when switching
      this.pronunciationResult = null
      this.transcriptionResult = null
      this.dictation = this.createEmptyDictationState()
    },
    
    // ===== Dictation Mode (EN) =====
    createEmptyDictationState() {
      return {
        queue: [],
        index: 0,
        current: null,
        answer: '',
        lastScore: null,
        replaySpeed: 1.0,
        isSlow: false,
        itemsCorrect: 0,
        itemsAttempted: 0
      }
    },
    
    ensureDictationQueue() {
      if (!this.dictation) this.dictation = this.createEmptyDictationState()
      if (this.dictation.queue.length === 0 && this.practiceWords.length > 0) {
        // Build queue: prefer example_sentence, fall back to the word
        this.dictation.queue = this.practiceWords.map(w => ({
          id: w.id,
          prompt: w.example_sentence || w.word,
          word: w.word
        }))
        this.dictation.index = 0
        this.dictation.current = this.dictation.queue[0]
        this.dictation.answer = ''
      }
    },
    
    async playDictation(isSlow = false) {
      this.ensureDictationQueue()
      if (!this.dictation.current) return
      const speed = isSlow ? 0.85 : 1.0
      await languageLearningService.generateSpeech(this.dictation.current.prompt, 'en', speed)
    },
    
    scoreDictation(answer, target) {
      const normalize = s => s
        .toLowerCase()
        // Keep English letters, numbers, spaces, and apostrophes
        .replace(/[^a-z0-9\s']/gi, '')
        .trim()
      const a = normalize(answer).split(/\s+/)
      const b = normalize(target).split(/\s+/)
      const dp = Array.from({ length: b.length + 1 }, () => Array(a.length + 1).fill(0))
      for (let i = 0; i <= b.length; i++) dp[i][0] = i
      for (let j = 0; j <= a.length; j++) dp[0][j] = j
      for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
          dp[i][j] = Math.min(
            dp[i-1][j] + 1,
            dp[i][j-1] + 1,
            dp[i-1][j-1] + (b[i-1] === a[j-1] ? 0 : 1)
          )
        }
      }
      const wer = b.length ? (dp[b.length][a.length] / b.length) : 0
      return Math.max(0, Math.round((1 - wer) * 100))
    },
    
    submitDictation() {
      this.ensureDictationQueue()
      if (!this.dictation.current) return
      const target = this.dictation.current.prompt
      const score = this.scoreDictation(this.dictation.answer, target)
      this.dictation.lastScore = score
      this.dictation.itemsAttempted += 1
      if (score >= 80) this.dictation.itemsCorrect += 1
      // Update session counters
      this.sessionData.wordsAttempted += 1
      if (score >= 80) this.sessionData.correctPronunciations += 1
    },
    
    nextDictationItem() {
      this.ensureDictationQueue()
      if (this.dictation.index < this.dictation.queue.length - 1) {
        this.dictation.index += 1
      } else {
        this.dictation.index = 0
      }
      this.dictation.current = this.dictation.queue[this.dictation.index]
      this.dictation.answer = ''
      this.dictation.lastScore = null
    },
    
    selectWord(word) {
      this.currentWord = word
      this.currentWordIndex = this.practiceWords.findIndex(w => w.id === word.id)
      this.pronunciationResult = null
    },
    
    async playWord(word) {
      this.loading.audio = true
      try {
        const response = await languageLearningService.generateSpeech(
          word.word, 
          'en', 
          1.0
        )
        
        if (response.success) {
          await languageLearningService.playAudio(response.audioUrl)
        }
      } catch (error) {
        console.error('Error playing word:', error)
      } finally {
        this.loading.audio = false
      }
    },
    
    async toggleRecording() {
      if (this.isRecording) {
        await this.stopRecording()
      } else {
        await this.startRecording()
      }
    },
    
    async startRecording() {
      try {
        const recordingData = await languageLearningService.startRecording()
        this.mediaRecorder = recordingData.mediaRecorder
        this.audioChunks = recordingData.audioChunks
        this.stream = recordingData.stream
        
        this.mediaRecorder.start()
        this.isRecording = true
        
        // Auto stop after 10 seconds
        setTimeout(() => {
          if (this.isRecording) {
            this.stopRecording()
          }
        }, 10000)
        
      } catch (error) {
        console.error('Error starting recording:', error)
        toast.error('Could not start recording. Please check microphone permissions.')
      }
    },
    
    async stopRecording() {
      if (!this.mediaRecorder || !this.isRecording) return
      
      this.isRecording = false
      this.loading.speechProcessing = true
      
      try {
        const audioBlob = await languageLearningService.stopRecording(
          this.mediaRecorder, 
          this.audioChunks, 
          this.stream
        )
        
        // Validate pronunciation
        const response = await languageLearningService.validatePronunciation(
          audioBlob,
          this.userId,
          this.currentWord.id,
          this.currentWord.word
        )
        
        if (response.success) {
          this.pronunciationResult = response.analysis
          this.currentWord.accuracy_score = response.analysis.accuracy_score
          this.currentWord.completed = response.analysis.accuracy_score >= 70
          
          // Update session data
          this.sessionData.totalAttempts++
          this.sessionData.wordsAttempted++
          if (response.analysis.accuracy_score >= 70) {
            this.sessionData.correctPronunciations++
          }

          // Mastery tracking (>= 90%)
          if (response.analysis.accuracy_score >= 90 && !this.masteredWordIds.has(this.currentWord.id)) {
            this.masteredWordIds.add(this.currentWord.id)
            // Optimistically update UI progress
            if (this.userProgress) {
              this.userProgress.words_mastered = (this.userProgress.words_mastered || 0) + 1
            }
            // Persist minimal session update for immediate progress
            try {
              await languageLearningService.saveSession(this.userId, {
                session_type: 'pronunciation',
                duration_seconds: 5,
                words_practiced: 1,
                average_accuracy: response.analysis.accuracy_score,
                points_earned: 10,
                words_mastered: 1
              })
              // Refresh progress from backend in background
              this.loadProgress()
            } catch (e) { /* ignore */ }
          }
          
          // Show encouragement message
          this.addLunaMessage(response.analysis.encouragement || "Good job practicing! Keep it up! 🌟")
        }
        
      } catch (error) {
        console.error('Error processing recording:', error)
      } finally {
        this.loading.speechProcessing = false
        this.stopRecordingCleanup()
      }
    },
    
    stopRecordingCleanup() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
      this.mediaRecorder = null
      this.audioChunks = []
    },
    
    async practiceWithLuna() {
      if (!this.currentWord) return
      
      await this.playWord(this.currentWord)
      
      setTimeout(async () => {
        const message = `Now you try saying "${this.currentWord.word}". Take your time and speak clearly! 🎤`
        this.addLunaMessage(message)
        
        // Make Luna speak the encouragement
        await languageLearningService.speakLunaMessage(message, 'encouraging')
      }, 1000)
    },
    
    nextWord() {
      if (this.currentWordIndex < this.practiceWords.length - 1) {
        this.currentWordIndex++
        this.currentWord = this.practiceWords[this.currentWordIndex]
      } else {
        // Loop back to first word
        this.currentWordIndex = 0
        this.currentWord = this.practiceWords[0]
      }
      this.pronunciationResult = null
    },
    
    async sendChatMessage() {
      if (!this.chatInput.trim()) return
      
      const userMessage = this.chatInput.trim()
      this.addUserMessage(userMessage)
      this.chatInput = ''
      
      this.loading.lunaChat = true
      
      try {
        const response = await languageLearningService.chatWithLuna(
          this.userId,
          userMessage,
          {
            current_session: 'pronunciation',
            current_word: this.currentWord?.word,
            struggling_words: this.practiceWords
              .filter(w => w.accuracy_score && w.accuracy_score < 70)
              .map(w => w.word)
          }
        )
        
        if (response.success) {
          this.addLunaMessage(response.lunaResponse)
        } else {
          this.addLunaMessage("I'm having trouble right now. Please try again! 😊")
        }
        
      } catch (error) {
        console.error('Error chatting with Luna:', error)
        this.addLunaMessage("I'm having trouble right now. Please try again! 😊")
      } finally {
        this.loading.lunaChat = false
      }
    },
    
    // === VOICE RECORDING METHODS (Whisper Integration) ===
    
    async toggleVoiceRecording() {
      if (this.isVoiceRecording) {
        await this.stopVoiceRecording()
      } else {
        await this.startVoiceRecording()
      }
    },
    
    async startVoiceRecording() {
      try {
        // Check browser support
        if (!voiceRecordingService.isSupported) {
          toast.error('Voice recording is not supported in your browser')
          return
        }
        
        // Initialize recording
        await voiceRecordingService.initialize()
        
        // Set up event handlers
        voiceRecordingService.onStart = () => {
          this.isVoiceRecording = true
          toast.info('🎤 Listening... Speak to Luna!')
        }
        
        voiceRecordingService.onVolumeChange = (level) => {
          this.volumeLevel = level
        }
        
        voiceRecordingService.onStop = async () => {
          this.isVoiceRecording = false
          this.volumeLevel = 0
          await this.processVoiceRecording()
        }
        
        voiceRecordingService.onError = (error) => {
          console.error('Voice recording error:', error)
          this.isVoiceRecording = false
          this.volumeLevel = 0
          toast.error('Recording error. Please try again!')
        }
        
        // Start recording
        await voiceRecordingService.startRecording()
        
      } catch (error) {
        console.error('Failed to start voice recording:', error)
        this.isVoiceRecording = false
        
        if (error.message.includes('denied')) {
          toast.error('Microphone access denied. Please allow microphone access and try again.')
        } else {
          toast.error('Could not start recording. Please check your microphone.')
        }
      }
    },
    
    async stopVoiceRecording() {
      if (voiceRecordingService.isRecording) {
        voiceRecordingService.stopRecording()
      }
    },
    
    async processVoiceRecording() {
      try {
        this.loading.lunaChat = true
        toast.info('🔄 Processing your voice...')
        
        // Transcribe audio using Whisper
        const transcriptionResult = await voiceRecordingService.transcribeAudio('en', true)
        
        if (transcriptionResult.success && transcriptionResult.transcription && transcriptionResult.transcription.trim()) {
          // Add transcribed message as user message
          this.addUserMessage(`🎤 ${transcriptionResult.transcription}`)
          
          // Show Luna's encouragement if available
          if (transcriptionResult.luna_encouragement) {
            toast.success(transcriptionResult.luna_encouragement)
          }
          
          // Send transcribed text to Luna for response
          await this.sendTranscribedMessageToLuna(transcriptionResult.transcription)
          
        } else {
          // Handle transcription failure gracefully
          const fallbackMessage = transcriptionResult.fallback_message || 
                                 transcriptionResult.error ||
                                 'Sorry, I couldn\'t understand that. Could you try speaking again?'
          
          this.addLunaMessage(fallbackMessage, true, 'gentle')
          
          // Show appropriate toast based on the error
          if (transcriptionResult.error && transcriptionResult.error.includes('Could not understand')) {
            toast.warning('Could not understand your speech. Please try speaking more clearly!')
          } else {
            toast.warning('Voice processing issue. Please try again!')
          }
        }
        
      } catch (error) {
        console.error('Voice processing error:', error)
        this.addLunaMessage('I had trouble hearing you clearly. Could you type your message instead? 😊', true, 'gentle')
        toast.error('Voice processing failed. Please try again!')
      } finally {
        this.loading.lunaChat = false
      }
    },
    
    async sendTranscribedMessageToLuna(transcribedText) {
      try {
        const response = await languageLearningService.chatWithLuna(
          this.userId,
          transcribedText,
          {
            current_session: 'voice_chat',
            interaction_type: 'voice',
            current_word: this.currentWord?.word,
            struggling_words: this.practiceWords
              .filter(w => w.accuracy_score && w.accuracy_score < 70)
              .map(w => w.word)
          }
        )
        
        if (response.success) {
          // Add Luna's response and have her speak it
          this.addLunaMessage(response.lunaResponse, true, 'friendly')
        } else {
          this.addLunaMessage("I'm having a little trouble understanding. Let's try again! 😊", true, 'gentle')
        }
        
      } catch (error) {
        console.error('Error sending transcribed message to Luna:', error)
        this.addLunaMessage("I'm having trouble right now. Please try again! 😊", true, 'gentle')
      }
    },
    
    addUserMessage(content) {
      this.chatMessages.push({
        id: Date.now(),
        content,
        isUser: true
      })
      this.scrollChatToBottom()
    },
    
    addLunaMessage(content, speakMessage = false, emotion = 'friendly') {
      this.chatMessages.push({
        id: Date.now(),
        content,
        isUser: false
      })
      this.scrollChatToBottom()
      
      // Optionally make Luna speak the message
      if (speakMessage) {
        setTimeout(() => {
          languageLearningService.speakLunaMessage(content, emotion)
        }, 300)
      }
    },
    
    scrollChatToBottom() {
      this.$nextTick(() => {
        const chatContainer = this.$refs.chatMessages
        if (chatContainer) {
          chatContainer.scrollTop = chatContainer.scrollHeight
        }
      })
    },
    
    async refreshTips() {
      await this.loadTips()
    },
    
    getScoreClass(score) {
      if (score >= 90) return 'excellent'
      if (score >= 80) return 'good'
      if (score >= 70) return 'fair'
      return 'needs-improvement'
    },
    
    async saveSession() {
      const sessionDuration = Math.floor((new Date() - this.sessionStartTime) / 1000)
      const averageAccuracy = this.sessionData.totalAttempts > 0 
        ? (this.sessionData.correctPronunciations / this.sessionData.totalAttempts) * 100 
        : 0
      
      const sessionData = {
        session_type: 'pronunciation',
        duration_seconds: sessionDuration,
        words_practiced: this.sessionData.wordsAttempted,
        average_accuracy: averageAccuracy,
        points_earned: this.sessionData.correctPronunciations * 10
      }
      
      await languageLearningService.saveSession(this.userId, sessionData)
      // Refresh progress card after saving
      this.loadProgress()
    },

    /**
     * Voice Settings and Controls
     */
    async initializeVoiceSettings() {
      try {
        // Wait a moment for voice service to initialize
        setTimeout(() => {
          this.currentVoiceInfo = languageLearningService.getCurrentVoiceInfo()
          this.availableVoices = languageLearningService.getAvailableVoices()
          
          if (this.currentVoiceInfo.selectedVoice) {
            this.selectedVoiceName = this.currentVoiceInfo.selectedVoice.name
          }
          
          if (this.currentVoiceInfo.settings) {
            this.voiceSettings = { ...this.voiceSettings, ...this.currentVoiceInfo.settings }
          }
        }, 500)
      } catch (error) {
        console.error('Voice initialization error:', error)
      }
    },

    async changeVoice() {
      try {
        if (this.selectedVoiceName) {
          languageLearningService.updateVoiceSettings(this.selectedVoiceName, this.voiceSettings)
          this.currentVoiceInfo = languageLearningService.getCurrentVoiceInfo()
          
          // Preview the new voice
          await this.previewCurrentVoice()
        }
      } catch (error) {
        console.error('Voice change error:', error)
        toast.error('Could not change voice. Please try again!')
      }
    },

    updateVoiceSettings() {
      if (this.selectedVoiceName) {
        languageLearningService.updateVoiceSettings(this.selectedVoiceName, this.voiceSettings)
        this.currentVoiceInfo = languageLearningService.getCurrentVoiceInfo()
      }
    },

    async previewCurrentVoice() {
      this.loading.voicePreview = true
      try {
        if (this.selectedVoiceName) {
          await languageLearningService.previewVoice(this.selectedVoiceName)
        } else {
          // Preview with current settings
          await languageLearningService.speakLunaMessage(
            "Hi! I'm Luna! I love helping kids learn new words and have fun with language!",
            'friendly'
          )
        }
      } catch (error) {
        console.error('Voice preview error:', error)
        toast.error('Could not preview voice')
      } finally {
        this.loading.voicePreview = false
      }
    }
  }
}
</script>

<style scoped>
/* All existing styles remain the same, adding new ones below */

/* Current word display */
.current-word-display {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #06d6a0;
  margin-bottom: 20px;
}

/* Pronunciation result */
.pronunciation-result {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--border-color);
}

.accuracy-score {
  text-align: center;
  margin-bottom: 16px;
}

.score-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  margin: 0 auto;
}

.score-circle.excellent { background: #10b981; }
.score-circle.good { background: #06d6a0; }
.score-circle.fair { background: #f59e0b; }
.score-circle.needs-improvement { background: #ef4444; }

.score-number {
  font-size: 18px;
  font-weight: bold;
}

.feedback h6 {
  color: var(--text-primary);
  margin-bottom: 8px;
}

.feedback p {
  color: var(--text-secondary);
  margin-bottom: 12px;
}

.feedback ul {
  margin: 0;
  padding-left: 20px;
}

.feedback li {
  color: var(--text-secondary);
  margin-bottom: 4px;
}

/* Word item enhancements */
.word-item {
  cursor: pointer;
  transition: all 0.3s ease;
}

.word-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.word-item.active {
  border-color: #06d6a0;
  background: rgba(6, 214, 160, 0.1);
}

.word-content {
  flex: 1;
}

.word-definition {
  display: block;
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
}

.word-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.accuracy-badge {
  background: #06d6a0;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
}

/* Luna chat */
.luna-chat-card {
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow);
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.chat-title {
  color: var(--text-primary);
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 1px;
  margin-bottom: 16px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
  max-height: 200px;
  scrollbar-width: thin;
}

.chat-messages::-webkit-scrollbar {
  width: 4px;
}

.chat-messages::-webkit-scrollbar-track {
  background: var(--bg-tertiary);
}

.chat-messages::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 2px;
}

.chat-message {
  margin-bottom: 12px;
  display: flex;
}

.user-message {
  justify-content: flex-end;
}

.luna-message {
  justify-content: flex-start;
}

.message-content {
  max-width: 80%;
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.4;
}

.user-message .message-content {
  background: #06d6a0;
  color: white;
}

.luna-message .message-content {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.chat-input {
  display: flex;
  gap: 8px;
}

.chat-input input {
  flex: 1;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
}

.chat-input button {
  border-radius: 8px;
  padding: 8px 16px;
}

/* Voice Recording Styles */
.btn-voice {
  position: relative;
  min-width: 48px;
  padding: 8px 12px !important;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-voice:not(.recording):hover {
  transform: scale(1.05);
}

.btn-voice.recording {
  animation: pulse-recording 1.5s infinite;
  box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.3);
}

.recording-pulse {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  background: #ff4757;
  border-radius: 50%;
  animation: pulse-dot 1s infinite;
}

.volume-meter {
  display: flex;
  align-items: center;
  width: 60px;
  height: 4px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin: 0 8px;
  position: relative;
}

.volume-bar {
  height: 100%;
  background: linear-gradient(90deg, #28a745 0%, #ffc107 50%, #dc3545 100%);
  border-radius: 2px;
  transition: width 0.1s ease;
  min-width: 2px;
}

/* Voice Recording Animations */
@keyframes pulse-recording {
  0%, 100% {
    box-shadow: 0 0 0 4px rgba(220, 53, 69, 0.3);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(220, 53, 69, 0.1);
  }
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(1.2);
  }
}

/* Typing indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  align-items: center;
}

.typing-indicator span {
  width: 6px;
  height: 6px;
  background: var(--text-secondary);
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Enhanced microphone button */
.microphone-btn.processing {
  background: #f59e0b;
  animation: processing 1s infinite ease-in-out;
}

@keyframes processing {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

/* Stats grid */
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.stat-small {
  text-align: center;
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.stat-value {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: var(--text-primary);
}

.stat-small .stat-label {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Loading states */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Responsive enhancements */
@media (max-width: 768px) {
  .luna-chat-card {
    max-height: 300px;
  }
  
  .chat-messages {
    max-height: 150px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .word-item {
    padding: 12px 16px;
  }
  
  .word-definition {
    display: none;
  }
}

/* All previous CSS styles remain unchanged... */
/* Main Container */
.language-learning-page {
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  min-height: 100vh;
}

/* Luna Header */
.luna-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 24px 0;
  color: white;
}

.luna-header h4 {
  color: white !important;
}

.luna-header p {
  color: rgba(255, 255, 255, 0.9) !important;
}

.luna-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.luna-character {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #fbbf24 100%);
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  animation: gentle-glow 3s ease-in-out infinite alternate;
}

.luna-face {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
}

.luna-eyes {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
}

.eye {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  position: relative;
  animation: blink 4s infinite;
}

.eye::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 4px;
  height: 4px;
  background: #1f2937;
  border-radius: 50%;
}

.luna-mouth {
  font-size: 16px;
  animation: smile 2s ease-in-out infinite alternate;
}

.luna-sparkles {
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  pointer-events: none;
}

.sparkle {
  position: absolute;
  font-size: 12px;
  animation: twinkle 2s infinite;
}

.sparkle-1 {
  top: 10%;
  right: 15%;
  animation-delay: 0s;
}

.sparkle-2 {
  bottom: 15%;
  left: 10%;
  animation-delay: 0.7s;
}

.sparkle-3 {
  top: 60%;
  right: 5%;
  animation-delay: 1.4s;
}

@keyframes gentle-glow {
  0% {
    box-shadow: 0 0 20px rgba(168, 85, 247, 0.4);
  }
  100% {
    box-shadow: 0 0 30px rgba(236, 72, 153, 0.6);
  }
}

@keyframes blink {
  0%, 90%, 100% {
    transform: scaleY(1);
  }
  95% {
    transform: scaleY(0.1);
  }
}

@keyframes smile {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.1);
  }
}

@keyframes twinkle {
  0%, 100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

.level-selector {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 8px;
  padding: 8px 16px;
}

.level-selector option {
  background: var(--bg-primary);
  color: var(--text-primary);
}

/* Learning Tabs */
.learning-tabs {
  display: flex;
  gap: 16px;
  padding: 0 24px;
}

.tab-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
}

.tab-button.active {
  background: #06d6a0;
  border-color: #06d6a0;
  color: white;
}

.tab-button:hover:not(.active) {
  background: rgba(255, 255, 255, 0.3);
}

/* Main Content */
.container-fluid {
  padding: 24px;
}

/* Practice Section */
.practice-section {
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 32px;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
}

.section-title {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  margin: 0;
  text-align: center;
  font-weight: bold;
}

/* Word List */
.word-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.word-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  transition: all 0.3s ease;
  background: var(--bg-secondary);
}

.word-item.completed {
  border-color: #10b981;
  background: var(--role-light, #f0fdf4);
}

.word-text {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.btn-play {
  background: #06d6a0;
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

/* Voice Interface */
.voice-interface {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  border: 1px solid var(--border-color);
}

.voice-interface p {
  color: var(--text-primary);
}

.microphone-btn {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #06d6a0;
  border: none;
  color: white;
  font-size: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 8px 32px rgba(6, 214, 160, 0.3);
}

.microphone-btn:hover {
  transform: scale(1.05);
}

.microphone-btn.recording {
  background: #ef4444;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(239, 68, 68, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 24px;
}

.btn-secondary {
  background: #f87171;
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 20px;
  font-weight: 600;
}

.btn-primary {
  background: #10b981;
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 20px;
  font-weight: 600;
}

/* Sidebar Cards */
.tips-card, .progress-card {
  background: var(--bg-primary);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow);
}

.tips-title, .progress-title {
  color: var(--text-primary);
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 1px;
}

/* Voice Settings Panel */
.voice-settings-panel {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.voice-panel-content {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.voice-title {
  color: white;
  font-weight: bold;
  margin-bottom: 16px;
}

.voice-settings-btn {
  transition: all 0.3s ease;
}

.voice-settings-btn.active {
  background: #06d6a0;
  border-color: #06d6a0;
  color: white;
}

.voice-settings-btn:hover {
  background: #06d6a0;
  border-color: #06d6a0;
  color: white;
  transform: scale(1.05);
}

/* Voice Controls */
.voice-selection .form-select,
.voice-parameters .form-range {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #333;
}

.voice-selection .form-select:focus,
.voice-parameters .form-range:focus {
  border-color: #06d6a0;
  box-shadow: 0 0 0 0.2rem rgba(6, 214, 160, 0.25);
}

.voice-preview {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.voice-preview .btn-primary {
  background: #06d6a0;
  border-color: #06d6a0;
}

.voice-preview .btn-primary:hover {
  background: #05c5a0;
  border-color: #05c5a0;
  transform: scale(1.05);
}

/* Enhanced Luna Character */
.luna-character {
  position: relative;
  animation: lunaFloat 3s ease-in-out infinite;
}

@keyframes lunaFloat {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
}

.tips-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tips-list li {
  padding: 8px 0;
  color: var(--text-secondary);
  font-size: 14px;
  border-bottom: 1px solid var(--border-color);
}

.tips-list li:last-child {
  border-bottom: none;
}

/* Progress Stats */
.stat-item {
  text-align: center;
  margin-bottom: 16px;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: bold;
  color: var(--text-primary);
}

.stat-label {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
}

.progress-bar-container {
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
  border: 1px solid var(--border-color);
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%);
  transition: width 0.3s ease;
}

.level-badge {
  text-align: center;
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  border: 1px solid var(--border-color);
}

/* Streak Badge */
.streak-badge {
  position: absolute;
  top: -10px;
  right: -10px;
  background: linear-gradient(135deg, #ec4899 0%, #f43f5e 100%);
  color: white;
  padding: 8px 12px;
  border-radius: 50%;
  text-align: center;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.3);
  transform: rotate(12deg);
}

.progress-card {
  position: relative;
}

.streak-number {
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
}

.streak-label {
  font-size: 10px;
  line-height: 1;
}

/* Responsive Design */
@media (max-width: 768px) {
  .learning-tabs {
    flex-direction: column;
    padding: 0 16px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .microphone-btn {
    width: 100px;
    height: 100px;
    font-size: 40px;
  }
}
</style> 